/**
 * The round reducer. Renderers call apply() and animate the returned events;
 * they never re-implement rules. Illegal actions leave the state untouched
 * and return a single 'illegal' event (the client does not log them).
 */
import { Greedy, playGreedy, type PolicyParams } from './policy.js';
import { rngSeed, rngSeedFrom, rngShuffle } from './rng.js';
import {
  BAG_MAX,
  PIECE_BOARD,
  bagSnapshotOf,
  cloneRound,
  doMagnet,
  doTap,
  doUndo,
  fail,
  isLocked,
  isTappable,
  listTappable,
  magnetPick,
  settle,
  undoPartner,
  type Round,
} from './state.js';
import { Booster, FailReason, Op, Status, type Action, type RoundEvent } from './types.js';

const SHUFFLE_POLICY: PolicyParams = { eps: 250, topK: 3 };

function illegal(reason: string): RoundEvent[] {
  return [{ type: 'illegal', reason }];
}

/** Can the round still be won from here (taps only)? Deterministic, bounded by run count. */
export function quickSolvable(r: Round, runs: number, seed: number): boolean {
  const rng = rngSeed(seed, 'check');
  const g = new Greedy(r.L.n, r.L.items.length);
  for (let k = 0; k < runs; k++) {
    const c = playGreedy(cloneRound(r), rng, SHUFFLE_POLICY, g);
    if (c.status === Status.Won) return true;
  }
  return false;
}

export function canUndo(r: Round): boolean {
  return r.status === Status.Playing && r.bagLen > 0;
}
/**
 * The board tiles a shuffle permutes: uncovered and not padlocked. While the locks are shut a
 * locked tile keeps its item, so the key item never lands on a locked tile (and the key is
 * identified by its item type throughout the round). CollectGoal golden markers belong to pieces,
 * not item labels, so they stay on the same tile indices while these labels are permuted. Only
 * board tiles are permuted: conveyor and dispenser items keep their order, a tray's three
 * pieces are not board tiles, so a tray keeps its item (it stays a triple of one type), and a
 * shelf's row keeps its items in place (its ends stay the items the player planned for).
 */
function shuffleTiles(r: Round): number[] {
  const tiles: number[] = [];
  for (let i = 0; i < r.L.nBoard; i++) if (r.piece[i] === PIECE_BOARD && r.cover[i] === 0 && !isLocked(r, i)) tiles.push(i);
  return tiles;
}
export function canShuffle(r: Round): boolean {
  return r.status === Status.Playing && shuffleTiles(r).length >= 2;
}
export function canMagnet(r: Round): boolean {
  return r.status === Status.Playing && magnetPick(r) !== null;
}
export function canExtraSlot(r: Round): boolean {
  return r.status === Status.Playing && !r.extraSlotUsed;
}

export function apply(r: Round, a: Action): RoundEvent[] {
  if (!Number.isInteger(a.t) || a.t < r.lastT || a.t > 0xffffffff) return illegal('time');
  const ev: RoundEvent[] = [];
  // Rush timer: any action at or after the deadline acts as the timer tick. It is
  // a legal action (the client logs it) that fails the round and does nothing else,
  // so live play and server replay reach the same state.
  if (r.status === Status.Playing && r.deadline > 0 && a.t >= r.deadline) {
    r.lastT = a.t;
    fail(r, FailReason.Timeout, ev);
    return ev;
  }
  switch (a.op) {
    case Op.Tick:
      if (r.status !== Status.Playing) return illegal('status');
      r.lastT = a.t;
      return ev;
    case Op.Tap: {
      if (r.status !== Status.Playing) return illegal('status');
      const i = a.arg ?? -1;
      if (!Number.isInteger(i) || i < 0 || i >= r.L.n) return illegal('tile');
      if (!isTappable(r, i)) return illegal(r.piece[i] === PIECE_BOARD && r.cover[i] === 0 && isLocked(r, i) ? 'locked' : 'covered');
      doTap(r, i, a.t, ev);
      return ev;
    }
    case Op.Undo: {
      if (!canUndo(r)) return illegal('undo');
      const tile = doUndo(r);
      const partner = undoPartner(r, tile);
      r.lastT = a.t;
      r.boosters |= Booster.Undo;
      ev.push(partner >= 0 ? { type: 'undone', tile, bag: bagSnapshotOf(r), partner } : { type: 'undone', tile, bag: bagSnapshotOf(r) });
      return ev;
    }
    case Op.Shuffle: {
      if (!canShuffle(r)) return illegal('shuffle');
      const tiles = shuffleTiles(r);
      const original = tiles.map((i) => r.item[i]!);
      const rngBefore = r.rng.slice();
      // one shared solver budget across tries keeps the check inside GP-06.2's 50 ms
      const runsPerTry = Math.max(1, Math.ceil(r.cfg.shuffleCheckRuns / r.cfg.shuffleTries));
      let chosen: number[] | null = null;
      for (let k = 0; k < r.cfg.shuffleTries && !chosen; k++) {
        const perm = rngShuffle(r.rng, original.slice());
        if (perm.every((v, j) => v === original[j])) continue; // identical layout is not a shuffle
        tiles.forEach((i, j) => (r.item[i] = perm[j]!));
        if (quickSolvable(r, runsPerTry, rngSeedFrom(r.rng))) chosen = perm;
      }
      if (!chosen) {
        // nothing solvable and different: leave the round exactly as it was; the booster is not spent
        tiles.forEach((i, j) => (r.item[i] = original[j]!));
        r.rng.set(rngBefore);
        return illegal('shuffle_unsolvable');
      }
      r.lastT = a.t;
      r.boosters |= Booster.Shuffle;
      tiles.forEach((i, j) => (r.item[i] = chosen![j]!));
      ev.push({ type: 'shuffled', changes: tiles.map((i) => [i, r.item[i]!] as [number, number]) });
      return ev;
    }
    case Op.Magnet: {
      if (!canMagnet(r)) return illegal('magnet');
      r.boosters |= Booster.Magnet;
      doMagnet(r, a.t, ev);
      return ev;
    }
    case Op.ExtraSlot: {
      if (!canExtraSlot(r)) return illegal('extraSlot');
      r.lastT = a.t;
      r.extraSlotUsed = true;
      r.boosters |= Booster.ExtraSlot;
      r.cap++;
      ev.push({ type: 'extraSlot', cap: r.cap });
      return ev;
    }
    case Op.RescueSlot: {
      if (r.status !== Status.Failed || !r.rescueEligible || r.failReason === FailReason.Timeout)
        return illegal('rescue');
      r.status = Status.Playing;
      r.failReason = FailReason.None;
      r.rescueEligible = false;
      r.outcome = null;
      r.rescues++;
      r.lastT = a.t;
      // grows the open bag; with slots still locked (bagLock) the bag they will add counts too, so
      // the bag never passes BAG_MAX once they open (without bagLock `locked` is 0: as before)
      if (r.cap + r.locked < BAG_MAX) r.cap++;
      // the last 3 collected items go back to the board; a glued pair goes back whole (up to 4)
      const returned: number[] = [];
      while (returned.length < 3) {
        const tile = doUndo(r);
        if (tile < 0) break;
        returned.push(tile);
        const partner = undoPartner(r, tile);
        if (partner >= 0) returned.push(partner);
      }
      ev.push({ type: 'rescued', kind: 'slot', cap: r.cap, deadline: r.deadline, returned });
      settle(r, ev);
      return ev;
    }
    case Op.RescueTime: {
      if (r.status !== Status.Failed || !r.rescueEligible || r.failReason !== FailReason.Timeout)
        return illegal('rescue');
      r.status = Status.Playing;
      r.failReason = FailReason.None;
      r.rescueEligible = false;
      r.outcome = null;
      r.rescues++;
      r.lastT = a.t;
      r.deadline = Math.max(r.deadline, a.t) + r.cfg.rescueTimeMs;
      ev.push({ type: 'rescued', kind: 'time', cap: r.cap, deadline: r.deadline, returned: [] });
      settle(r, ev);
      return ev;
    }
    default:
      return illegal('op');
  }
}

/** Tile indices the player can tap right now. */
export function tappableTiles(r: Round): number[] {
  const out = new Int32Array(r.L.n);
  const n = listTappable(r, out);
  return Array.from(out.subarray(0, n));
}

/** Whether a wrapped tile's item is shown: once uncovered it stays revealed. */
export function isRevealed(r: Round, i: number): boolean {
  return r.revealed[i] === 1 || r.piece[i] !== PIECE_BOARD;
}
